/**
 * Well — a pipeline stage inside the constellation canvas.
 * Renders its dots in a compact grid within its bounds.
 * Passes agent-acting state down to each Dot.
 */
import Dot from "./Dot";

// Layout constants (match the 3×3 grid in Constellation.jsx)
const DOTS_PER_ROW = 8;
const DOT_SPACING = 10; // px between dot centers
const PADDING = 12;

export default function Well({
  well,       // { id, label, x, y, width, height }
  dots,       // array of dot objects from API
  runningRuns = [], // agent_runs with status='running' and target_id
  onSelectDot,
}) {
  // Build a map of target_id → agent_id for acting runs
  const actingMap = {};
  for (const run of runningRuns) {
    if (run.target_id) {
      actingMap[run.target_id] = run.agent_id;
    }
  }

  // Position each dot in a grid inside the well
  const positionedDots = dots.map((dot, i) => {
    const col = i % DOTS_PER_ROW;
    const row = Math.floor(i / DOTS_PER_ROW);
    return {
      ...dot,
      agent_acting: actingMap[dot.id] || null,
      _x: well.x + PADDING + col * DOT_SPACING,
      _y: well.y + PADDING + 24 + row * DOT_SPACING, // 24px below label
    };
  });

  return (
    <g className="mc-well" role="group" aria-label={`${well.label} well`}>
      {/* Well background */}
      <rect
        x={well.x}
        y={well.y}
        width={well.width}
        height={well.height}
        rx={6}
        fill="rgba(255,255,255,0.03)"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={1}
      />

      {/* Well label */}
      <text
        x={well.x + PADDING}
        y={well.y + 14}
        fill="var(--color-slate-400)"
        fontSize={10}
        fontFamily="var(--font-sans)"
        letterSpacing="0.08em"
        textTransform="uppercase"
      >
        {well.label.toUpperCase()}
      </text>

      {/* Count badge */}
      <text
        x={well.x + well.width - PADDING}
        y={well.y + 14}
        fill="var(--color-slate-500)"
        fontSize={10}
        fontFamily="var(--font-mono)"
        textAnchor="end"
      >
        {dots.length}
      </text>

      {/* Dots */}
      {positionedDots.map((dot) => (
        <Dot
          key={dot.id}
          dot={dot}
          x={dot._x}
          y={dot._y}
          onSelect={onSelectDot}
        />
      ))}

      {/* Empty state */}
      {dots.length === 0 && (
        <text
          x={well.x + well.width / 2}
          y={well.y + well.height / 2 + 8}
          fill="var(--color-slate-600)"
          fontSize={10}
          fontFamily="var(--font-sans)"
          textAnchor="middle"
        >
          —
        </text>
      )}
    </g>
  );
}
